// Dior's Dough — public branded pay page for Instagram customers. Route: /pay/diorsdough
// Modes: menu + custom amount (bio link), or ?req=CODE (per-order link/QR from her register).
// Settlement: customer's own member wallet session -> POST /api/wallet/pay (Face ID when enrolled).
// Design intent: unmistakably HER site — black/tan bakery brand, photoreal product imagery,
// explicit notice that the visitor has left the BGNZN Institute (501(c)(6)) pages.
import { useCallback, useEffect, useRef, useState } from "react";
import { startAuthentication } from "@simplewebauthn/browser";

const API = "/api";
const CREAM = "#ecd9b8";
const TAN = "#c9a06c";

const fmtUsd = (n) =>
  n == null ? "" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PayDior() {
  const [profile, setProfile] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [reqCode] = useState(() => new URLSearchParams(window.location.search).get("req") || "");
  const [request, setRequest] = useState(null); // pre-priced per-order request

  const [cart, setCart] = useState({}); // item_id -> qty
  const [customAmount, setCustomAmount] = useState("");
  const [note, setNote] = useState("");
  const [outOfState, setOutOfState] = useState(false); // +$120 out-of-state order fee
  const OOS_FEE = 120;

  const [token, setToken] = useState(() => localStorage.getItem("wallet_token") || "");
  const [me, setMe] = useState(null);
  const [signin, setSignin] = useState({ ledger_uuid: "", phone: "" });
  const [step, setStep] = useState("shop"); // shop | signin | confirm | receipt
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [zelle, setZelle] = useState(null); // guest Zelle traverse declaration
  const [copied, setCopied] = useState("");
  const [cardCfg, setCardCfg] = useState(null); // PayPal card rail config
  const cardRef = useRef(null);
  const appleRef = useRef(null);
  const appleCfgRef = useRef(null); // { applepay, cfg } once the device+merchant prove eligible
  const [appleEligible, setAppleEligible] = useState(false);

  const startZelle = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/storefront/diorsdough/zelle-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount_usd: amount, memo, items: cartItems.length ? cartItems : undefined }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Could not open Zelle checkout");
      setZelle(d);
      setStep("zelle");
    } catch (err) {
      setError(err.message);
      // fault beacon: the desk hears about a failed checkout within seconds, not after the sale is lost
      try {
        await fetch(`${API}/storefront/diorsdough/zelle-error`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ detail: String(err.message || err).slice(0, 240) }),
        });
      } catch { /* beacon is best-effort */ }
    } finally {
      setBusy(false);
    }
  };

  // watch the funding desk: when the treasury officer confirms the Zelle, this flips to the receipt
  useEffect(() => {
    if (step !== "zelle" || !zelle?.intention_id) return;
    const t = setInterval(async () => {
      try {
        const r = await fetch(`${API}/storefront/diorsdough/zelle-status/${zelle.intention_id}`);
        if (r.ok) {
          const d = await r.json();
          if (d.status === "SETTLED") setStep("zelle-done");
        }
      } catch { /* keep watching */ }
    }, 5000);
    return () => clearInterval(t);
  }, [step, zelle?.intention_id]);

  const copyText = async (text, tag) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      setTimeout(() => setCopied(""), 2000);
    } catch { /* clipboard blocked — the text is selectable */ }
  };

  useEffect(() => {
    fetch(`${API}/storefront/diorsdough`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Storefront unavailable");
        setProfile(d);
      })
      .catch((e) => setLoadError(e.message));
  }, []);

  useEffect(() => {
    if (!reqCode) return;
    fetch(`${API}/storefront/pay-request/${encodeURIComponent(reqCode)}`)
      .then(async (r) => {
        const d = await r.json();
        if (r.ok) setRequest(d);
      })
      .catch(() => {});
  }, [reqCode]);

  const loadMe = useCallback(async (t) => {
    const res = await fetch(`${API}/wallet/me`, { headers: { Authorization: `Bearer ${t}` } });
    if (res.ok) setMe(await res.json());
    else {
      localStorage.removeItem("wallet_token");
      setToken("");
    }
  }, []);

  useEffect(() => {
    if (token) loadMe(token);
  }, [token, loadMe]);

  const cartLines = (profile?.menu || []).filter((m) => (cart[m.item_id] || 0) > 0);
  const cartItems = cartLines.map((m) => ({ item_id: m.item_id, qty: cart[m.item_id] }));
  const cartTotal = Math.round(cartLines.reduce((s, m) => s + m.price_usd * cart[m.item_id], 0) * 100) / 100;
  const base = request ? (request.amount_usd ?? (parseFloat(customAmount) || null))
                       : cartLines.length ? cartTotal : (parseFloat(customAmount) || null);
  const fee = !request && outOfState && base ? OOS_FEE : 0;
  const amount = base != null ? base + fee : null;
  const cartMemo = cartLines.map((m) => `${cart[m.item_id]}× ${m.name}`).join(" · ");
  const memoParts = request ? [request.memo || "Dior's Dough order"]
                            : [cartLines.length ? [cartMemo, note].filter(Boolean).join(" — ") : (note || "Dior's Dough order")];
  if (fee) memoParts.push("out-of-state delivery +$120");
  const memo = memoParts.join(" · ");

  const startApplePay = () => {
    const ctx = appleCfgRef.current;
    if (!ctx || !amount || amount <= 0) return;
    const { applepay, cfg } = ctx;
    const paymentRequest = {
      countryCode: cfg.countryCode,
      merchantCapabilities: cfg.merchantCapabilities,
      supportedNetworks: cfg.supportedNetworks,
      currencyCode: "USD",
      requiredBillingContactFields: ["postalAddress"],
      total: { label: "Dior's Dough", type: "final", amount: amount.toFixed(2) },
    };
    const session = new window.ApplePaySession(4, paymentRequest);
    session.onvalidatemerchant = (event) => {
      applepay.validateMerchant({ validationUrl: event.validationURL, displayName: "Dior's Dough" })
        .then((r) => session.completeMerchantValidation(r.merchantSession))
        .catch(() => session.abort());
    };
    session.onpaymentauthorized = (event) => {
      (async () => {
        try {
          const res = await fetch(`${API}/storefront/diorsdough/card-order`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ amount_usd: amount, memo, items: cartItems.length ? cartItems : undefined }),
          });
          const d = await res.json();
          if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Card checkout could not start");
          await applepay.confirmOrder({
            orderId: d.order_id, token: event.payment.token, billingContact: event.payment.billingContact,
          });
          session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
          const cap = await fetch(`${API}/storefront/diorsdough/card-capture`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ order_id: d.order_id }),
          });
          const cd = await cap.json();
          if (!cap.ok) throw new Error(typeof cd.detail === "string" ? cd.detail : "Capture failed");
          setReceipt(cd);
          setStep("receipt");
        } catch (err) {
          session.completePayment(window.ApplePaySession.STATUS_FAILURE);
          setError(err.message || "Apple Pay could not complete — the card button works as a fallback.");
        }
      })();
    };
    session.begin();
  };

  // guest card rail (PayPal): the SDK and the buttons load only when the backend says the rail
  // is configured. Card data lives entirely inside PayPal's sheet — this page never sees a number.
  useEffect(() => {
    fetch(`${API}/storefront/diorsdough/card-config`)
      .then((r) => r.json())
      .then(setCardCfg)
      .catch(() => setCardCfg({ enabled: false }));
  }, []);

  useEffect(() => {
    if (!cardCfg?.enabled || !amount || amount <= 0 || step !== "shop" || request?.status === "PAID") return;
    const setupApplePay = () => {
      try {
        if (appleCfgRef.current) return;
        if (!window.ApplePaySession || !window.ApplePaySession.canMakePayments() || !window.paypal?.Applepay) return;
        const applepay = window.paypal.Applepay();
        applepay.config().then((cfg) => {
          if (!cfg?.isEligible) return;
          appleCfgRef.current = { applepay, cfg };
          if (!document.getElementById("apple-pay-sdk")) {
            const a = document.createElement("script");
            a.id = "apple-pay-sdk";
            a.src = "https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js";
            a.onload = () => setAppleEligible(true);
            document.head.appendChild(a);
          } else {
            setAppleEligible(true);
          }
        }).catch(() => {});
      } catch { /* no Apple Pay on this device — the card button carries on */ }
    };
    const renderButtons = () => {
      if (!window.paypal || !cardRef.current) return;
      cardRef.current.innerHTML = "";
      setupApplePay();
      window.paypal.Buttons({
        fundingSource: window.paypal.FUNDING.CARD,
        style: { layout: "vertical", color: "black", shape: "pill", label: "pay", height: 45 },
        createOrder: async () => {
          const res = await fetch(`${API}/storefront/diorsdough/card-order`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ amount_usd: amount, memo, items: cartItems.length ? cartItems : undefined }),
          });
          const d = await res.json();
          if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Card checkout could not start");
          return d.order_id;
        },
        onApprove: async (data) => {
          try {
            const res = await fetch(`${API}/storefront/diorsdough/card-capture`, {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ order_id: data.orderID }),
            });
            const d = await res.json();
            if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Card payment failed");
            setReceipt(d);
            setStep("receipt");
          } catch (err) { setError(err.message); }
        },
        onError: () => setError("Card checkout hit a snag — Zelle works as a fallback."),
      }).render(cardRef.current);
    };
    if (window.paypal) { renderButtons(); return; }
    const s = document.createElement("script");
    s.src = `https://www.paypal.com/sdk/js?client-id=${cardCfg.client_id}&currency=USD&components=buttons,applepay&enable-funding=card&intent=capture`;
    s.onload = renderButtons;
    s.onerror = () => setCardCfg((c) => ({ ...c, enabled: false }));
    document.head.appendChild(s);
  }, [cardCfg, amount, memo, step, request]);

  // Apple's web component is injected imperatively — React doesn't know the tag
  useEffect(() => {
    if (!appleEligible || !appleRef.current) return;
    appleRef.current.innerHTML = '<apple-pay-button data-testid="applepay-button" buttonstyle="black" type="buy" locale="en-US" style="display:block;width:100%;height:45px;--apple-pay-button-border-radius:999px;"></apple-pay-button>';
    const btn = appleRef.current.querySelector("apple-pay-button");
    if (btn) btn.addEventListener("click", startApplePay);
    return () => { if (btn) btn.removeEventListener("click", startApplePay); };
  }, [appleEligible, amount]);

  const doSignin = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await fetch(`${API}/wallet/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(signin),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Sign-in failed — check your details");
      localStorage.setItem("wallet_token", d.token);
      setToken(d.token);
      await loadMe(d.token);
      setStep("confirm");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const doPay = async () => {
    setBusy(true);
    setError("");
    try {
      try {  // tell the merchant a customer reached checkout — fire and forget, never blocks payment
        fetch(`${API}/storefront/diorsdough/intent`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ amount_usd: amount, memo, items: cartItems.length ? cartItems : undefined }),
        });
      } catch { /* intent ping is best-effort */ }
      const body = request
        ? { request_code: request.code }
        : { to_address: profile.payee_address, amount_usd: amount, memo };
      if (request && request.amount_usd == null) body.amount_usd = amount;
      if (me?.passkey_enrolled) {
        const optRes = await fetch(`${API}/wallet/passkey/pay-options`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({
            request_code: request?.code,
            to_address: request ? undefined : profile.payee_address,
            amount_usd: amount,
            origin_url: window.location.origin,
          }),
        });
        const opt = await optRes.json();
        if (!optRes.ok) throw new Error(typeof opt.detail === "string" ? opt.detail : "Could not start Face ID approval");
        const credential = await startAuthentication({ optionsJSON: opt.options });
        body.passkey = { binding_id: opt.binding_id, credential, origin_url: window.location.origin };
      }
      const res = await fetch(`${API}/wallet/pay`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) {
        const det = d?.detail;
        throw new Error(typeof det === "string" ? det : det?.message || det?.code || "Payment failed");
      }
      setReceipt(d);
      setStep("receipt");
    } catch (err) {
      setError(err.name === "NotAllowedError" ? "Face ID approval cancelled — payment not sent." : err.message);
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="min-h-[100dvh] bg-[#0c0b09] flex items-center justify-center px-6" data-testid="paydior-page">
        <p className="text-sm text-center" style={{ color: CREAM }} data-testid="storefront-error">{loadError}</p>
      </div>
    );
  }
  if (!profile) {
    return <div className="min-h-[100dvh] bg-[#0c0b09]" data-testid="paydior-page" />;
  }

  return (
    <div className="min-h-[100dvh] bg-[#0c0b09] text-stone-200 flex flex-col items-center pb-10" data-testid="paydior-page">
      {/* the baker herself, soft in the backdrop — warm blur, never competing with content */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <img src="/assets/diors/baker-counter-v2-b.png" alt="" data-testid="baker-backdrop"
             className="w-full h-full object-cover scale-110 blur-2xl opacity-[0.14]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c0b09]/70 via-transparent to-[#0c0b09]/80" />
      </div>
      <div className="relative z-10 w-full flex flex-col items-center">
      {/* photoreal hero — her world, not the org's */}
      <div className="relative w-full max-w-2xl">
        <div className="relative h-44 sm:h-56 w-full overflow-hidden">
          <img src={profile.hero} alt="Fresh sourdough cinnamon rolls at Dior's Dough"
               className="w-full h-full object-cover" data-testid="paydior-hero" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-[#0c0b09]" />
        </div>
        <div className="absolute -bottom-12 left-1/2 -translate-x-1/2 rounded-full p-1 bg-[#0c0b09]" style={{ border: `3px solid ${TAN}` }}>
          <img src={profile.logo} alt="Dior's Dough logo" data-testid="paydior-logo"
               className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover" />
        </div>
      </div>

      <div className="w-full max-w-md px-4">
        <header className="flex flex-col items-center pt-16 pb-5 text-center">
          <h1 className="text-3xl font-bold tracking-wide" style={{ fontFamily: "Georgia, 'Times New Roman', serif", color: CREAM }}>
            {profile.brand}
          </h1>
          <p className="mt-1 text-xs text-stone-400 max-w-xs">{profile.tagline}</p>
          <p className="mt-2 text-[11px] font-mono tracking-widest uppercase" style={{ color: TAN }}>
            @{profile.instagram} · {profile.location}
          </p>
          <p className="mt-3 rounded-full border px-3 py-1 text-[9px] font-mono tracking-widest uppercase text-stone-500"
             style={{ borderColor: "#3a352c" }} data-testid="site-separation-notice">
            Independent business — you have left the BGNZN Institute site
          </p>
          {/* a hint of her in the forefront — full feature card */}
          <div className="mt-4 rounded-2xl border overflow-hidden" style={{ borderColor: "#3a352c" }} data-testid="baker-hint">
            <img src="/assets/diors/dior-apron-portrait.jpeg" alt="Dior, your baker, in her Dior's Dough apron" data-testid="baker-portrait"
                 className="w-full h-72 object-cover" style={{ objectPosition: "center 18%" }} />
            <div className="flex items-center justify-between px-4 py-3" style={{ background: "rgba(236,217,184,0.07)" }}>
              <div>
                <p className="text-[9px] font-mono tracking-[0.25em] uppercase" style={{ color: TAN }}>Your baker</p>
                <p className="text-lg font-bold leading-tight" style={{ fontFamily: "Georgia, serif", color: CREAM }}>Serenity "Dior"</p>
              </div>
              <img src={profile.logo} alt="" className="w-10 h-10 rounded-full object-cover bg-[#0c0b09]" style={{ border: `2px solid ${TAN}` }} />
            </div>
            <p className="px-4 pb-3 text-[11px] text-left leading-snug text-stone-300" style={{ background: "rgba(236,217,184,0.07)" }}>
              Baked by <span className="font-bold" style={{ color: CREAM }}>Dior</span> herself — apron on, sleeves up, reading every note
            </p>
          </div>
        </header>

        {/* meet the woman behind the brand — biography first, family as a whisper */}
        <section className="mb-6 rounded-2xl border overflow-hidden" style={{ borderColor: "#3a352c" }} data-testid="about-baker">
          <div className="p-4" style={{ background: "rgba(236,217,184,0.04)" }}>
            <p className="text-[10px] font-mono tracking-widest uppercase" style={{ color: TAN }}>Meet your baker</p>
            <p className="mt-2 text-xs leading-relaxed text-stone-300" data-testid="baker-bio">
              Serenity, better known as Dior, is an artist in the kitchen as well as on the stage — able to
              carry a tray full of hot sourdough cinnamon rolls or a tune with equal grace. Serenity brings a
              nostalgic feeling and flavor back to the world of baking; as a full-time mother she blends
              family and career eloquently. She's excited to share her secrets of the trade with all of you!
            </p>
            <div className="mt-3 flex items-center gap-2">
              <img src="/assets/diors/family-western.jpeg" alt="Dior with her family"
                   className="w-14 h-14 rounded-lg object-cover object-top opacity-90" data-testid="about-photo-hug" />
              <img src="/assets/diors/family-couple-branded.png" alt="Dior and family in Dior's Dough tees"
                   className="w-14 h-14 rounded-lg object-cover opacity-90" data-testid="about-photo-family" />
              <p className="ml-1 text-[10px] text-stone-500 leading-snug">From our family kitchen<br />to yours</p>
            </div>
          </div>
        </section>

        {error && (
          <div data-testid="paydior-error" className="mb-4 rounded-lg border border-red-500/50 bg-red-950/30 px-4 py-3 text-xs text-red-300">
            {error}
          </div>
        )}

        {/* shop step */}
        {step === "shop" && (
          <div className="space-y-5" data-testid="paydior-shop">
            {request && (
              <div className="rounded-xl border px-4 py-3 text-center" style={{ borderColor: TAN, background: "rgba(201,160,108,0.08)" }} data-testid="paydior-request-banner">
                {request.status === "PAID" ? (
                  <p className="text-sm" style={{ color: CREAM }}>This order link has already been paid. Thank you!</p>
                ) : (
                  <>
                    <p className="text-[10px] font-mono tracking-widest uppercase" style={{ color: TAN }}>Your order from {profile.brand}</p>
                    <p className="mt-1 text-sm" style={{ color: CREAM }} data-testid="request-memo">{request.memo || "Order"}</p>
                    {request.amount_usd != null && (
                      <p className="mt-1 text-2xl font-bold" style={{ fontFamily: "Georgia, serif", color: CREAM }}>{fmtUsd(request.amount_usd)}</p>
                    )}
                  </>
                )}
              </div>
            )}

            {!request && (
              <div className="grid grid-cols-1 gap-2.5" data-testid="paydior-menu">
                {profile.menu.map((m) => {
                  const qty = cart[m.item_id] || 0;
                  return (
                    <div
                      key={m.item_id}
                      data-testid={`menu-item-${m.item_id}`}
                      className="flex items-center gap-3 rounded-xl border p-2.5 transition-colors"
                      style={qty > 0
                        ? { borderColor: CREAM, background: "rgba(236,217,184,0.12)" }
                        : { borderColor: "#3a352c", background: "rgba(255,255,255,0.02)" }}
                    >
                      {m.image && (
                        <img src={m.image} alt={m.name} className="w-16 h-16 rounded-lg object-cover flex-none"
                             style={{ border: `1px solid ${qty > 0 ? CREAM : "#3a352c"}` }} />
                      )}
                      <span className="flex-1 text-sm leading-snug" style={{ color: CREAM }}>{m.name}</span>
                      <span className="text-sm font-mono flex-none" style={{ color: TAN }}>{fmtUsd(m.price_usd)}</span>
                      {qty === 0 ? (
                        <button
                          data-testid={`cart-add-${m.item_id}`}
                          onClick={() => { setCart({ ...cart, [m.item_id]: 1 }); setCustomAmount(""); }}
                          className="flex-none rounded-full px-3.5 py-1.5 text-[10px] font-bold tracking-widest transition-transform active:scale-95"
                          style={{ background: CREAM, color: "#241a10" }}
                        >ADD</button>
                      ) : (
                        <span className="flex-none flex items-center gap-2">
                          <button
                            data-testid={`cart-minus-${m.item_id}`}
                            aria-label={`one less ${m.name}`}
                            onClick={() => { const next = { ...cart }; if (qty <= 1) { delete next[m.item_id]; } else { next[m.item_id] = qty - 1; } setCart(next); }}
                            className="w-7 h-7 rounded-full border text-sm leading-none transition-transform active:scale-90"
                            style={{ borderColor: "#3a352c", color: CREAM }}
                          >−</button>
                          <span className="font-mono text-sm w-5 text-center" data-testid={`cart-qty-${m.item_id}`} style={{ color: CREAM }}>{qty}</span>
                          <button
                            data-testid={`cart-plus-${m.item_id}`}
                            aria-label={`one more ${m.name}`}
                            onClick={() => setCart({ ...cart, [m.item_id]: qty + 1 })}
                            className="w-7 h-7 rounded-full border text-sm leading-none transition-transform active:scale-90"
                            style={{ borderColor: "#3a352c", color: CREAM }}
                          >+</button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {!request && cartLines.length > 0 && (
              <div data-testid="cart-summary" className="rounded-xl border p-3 text-xs font-mono leading-relaxed"
                style={{ borderColor: "#3a352c", color: TAN }}>
                {cartMemo} — <span data-testid="cart-total" style={{ color: CREAM }}>{fmtUsd(cartTotal)}</span>
              </div>
            )}

            {request?.status !== "PAID" && (
              <div className="space-y-3">
                {(request?.amount_usd == null) && (
                  <div>
                    <label className="text-[10px] font-mono tracking-widest uppercase" style={{ color: TAN }}>
                      {request ? "Amount" : "Or a custom amount"}
                    </label>
                    <input
                      data-testid="custom-amount-input"
                      type="number" step="0.01" min="0.01" placeholder="0.00"
                      value={customAmount}
                      onChange={(e) => { setCustomAmount(e.target.value); setCart({}); }}
                      className="mt-1 w-full rounded-xl border bg-[#12100c] px-4 py-3 text-lg font-mono focus:outline-none"
                      style={{ borderColor: "#3a352c", color: CREAM }}
                    />
                  </div>
                )}
                {!request && (
                  <input
                    data-testid="note-input"
                    type="text" placeholder="Note for Dior (flavor, pickup day…)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="w-full rounded-xl border bg-[#12100c] px-4 py-3 text-sm focus:outline-none"
                    style={{ borderColor: "#3a352c", color: CREAM }}
                  />
                )}
                {!request && (
                  <button
                    type="button"
                    data-testid="out-of-state-toggle"
                    onClick={() => setOutOfState((v) => !v)}
                    className="w-full flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors"
                    style={outOfState
                      ? { borderColor: CREAM, background: "rgba(236,217,184,0.12)" }
                      : { borderColor: "#3a352c", background: "rgba(255,255,255,0.02)" }}
                  >
                    <span className="text-xs" style={{ color: CREAM }}>Out-of-state order? Shipping &amp; handling applies</span>
                    <span className="text-xs font-mono" style={{ color: TAN }}>+$120.00</span>
                  </button>
                )}
                {(cart["membership"] || 0) > 0 && !request && (
                  <p className="rounded-xl border px-4 py-3 text-[11px] leading-relaxed text-stone-400" style={{ borderColor: "#3a352c" }} data-testid="membership-note">
                    Membership agreement — $200 each month and the per-order pricing disappears: sourdough
                    loaves and more, whenever you need them. Paying this starts your first month.
                  </p>
                )}
                {fee > 0 && (
                  <div className="flex justify-between text-xs font-mono px-1" data-testid="oos-fee-line">
                    <span className="text-stone-500">{fmtUsd(base)} + out-of-state fee</span>
                    <span style={{ color: TAN }}>{fmtUsd(amount)}</span>
                  </div>
                )}
                <button
                  data-testid="pay-continue-button"
                  disabled={!amount || amount <= 0 || busy}
                  onClick={() => (token && me ? setStep("confirm") : setStep("signin"))}
                  className="w-full rounded-xl py-4 text-sm font-bold uppercase tracking-widest transition-opacity disabled:opacity-40"
                  style={{ background: CREAM, color: "#0c0b09" }}
                >
                  {amount ? `Pay ${fmtUsd(amount)}` : "Pick something or enter an amount"}
                </button>
                <button
                  type="button"
                  data-testid="zelle-checkout-button"
                  disabled={!amount || amount <= 0 || busy}
                  onClick={startZelle}
                  className="w-full rounded-xl py-3.5 text-xs font-bold uppercase tracking-widest border transition-opacity disabled:opacity-40"
                  style={{ borderColor: TAN, color: CREAM, background: "rgba(201,160,108,0.10)" }}
                >
                  {busy ? "Opening…" : `No member wallet? Pay ${amount ? fmtUsd(amount) : ""} with Zelle`}
                </button>
                <p className="text-[10px] text-center text-stone-500" data-testid="zelle-hint">
                  Zelle from any bank app — no sign-in. Dior is texted the moment it settles.
                </p>
                {cardCfg?.enabled && amount > 0 && (
                  <div className="pt-1" data-testid="card-checkout-block">
                    <p className="text-[10px] text-center text-stone-500 mb-2">
                      …or pay by card{cardCfg.env !== "live" ? " — test mode while the account finishes review; no real charge" : ""}
                    </p>
                    <div ref={cardRef} data-testid="paypal-buttons" />
                    {appleEligible && <div ref={appleRef} data-testid="applepay-block" className="mt-2" />}
                  </div>
                )}
                {cardCfg && !cardCfg.enabled && (
                  <p className="text-[10px] text-center text-stone-600" data-testid="card-coming-soon">
                    Card checkout is being activated — Zelle works right now.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* signin step */}
        {step === "signin" && (
          <form onSubmit={doSignin} className="space-y-3 rounded-2xl border p-5" style={{ borderColor: "#3a352c" }} data-testid="paydior-signin">
            <div className="flex justify-center">
              <img src="/assets/diors/dior-tank-editorial.webp" alt="Dior" className="w-16 h-16 rounded-full object-cover object-top" style={{ border: `2px solid ${TAN}` }} data-testid="signin-baker" />
            </div>
            <h2 className="text-sm font-bold text-center" style={{ color: CREAM }}>Your member wallet</h2>
            <p className="text-xs text-stone-400 text-center">Ledger ID + registered phone — the same sign-in as your wallet.</p>
            <input
              data-testid="signin-uuid-input"
              type="text" placeholder="Ledger ID (UUID)" autoComplete="off"
              value={signin.ledger_uuid}
              onChange={(e) => setSignin({ ...signin, ledger_uuid: e.target.value })}
              className="w-full rounded-xl border bg-[#12100c] px-4 py-3 text-sm font-mono focus:outline-none"
              style={{ borderColor: "#3a352c", color: CREAM }}
            />
            <input
              data-testid="signin-phone-input"
              type="tel" placeholder="Registered phone"
              value={signin.phone}
              onChange={(e) => setSignin({ ...signin, phone: e.target.value })}
              className="w-full rounded-xl border bg-[#12100c] px-4 py-3 text-sm focus:outline-none"
              style={{ borderColor: "#3a352c", color: CREAM }}
            />
            <button
              data-testid="signin-submit-button"
              type="submit" disabled={busy}
              className="w-full rounded-xl py-3.5 text-sm font-bold uppercase tracking-widest disabled:opacity-50"
              style={{ background: CREAM, color: "#0c0b09" }}
            >
              {busy ? "Signing in…" : "Continue"}
            </button>
            <button type="button" onClick={() => setStep("shop")} className="w-full text-xs text-stone-500 hover:text-stone-300">← Back</button>
            <p className="text-[11px] text-center text-stone-500 pt-2">
              No wallet yet? Ask <span style={{ color: TAN }}>@{profile.instagram}</span> — she'll get you set up in a minute.
            </p>
          </form>
        )}

        {/* confirm step */}
        {step === "confirm" && (
          <div className="space-y-4 rounded-2xl border p-5" style={{ borderColor: "#3a352c" }} data-testid="paydior-confirm">
            <div className="flex justify-center">
              <img src={profile.logo} alt="" className="w-16 h-16 rounded-full object-cover" style={{ border: `2px solid ${TAN}` }} />
            </div>
            <div className="text-center">
              <p className="text-[10px] font-mono tracking-widest uppercase" style={{ color: TAN }}>Paying {profile.brand}</p>
              <p className="mt-1 text-3xl font-bold" style={{ fontFamily: "Georgia, serif", color: CREAM }} data-testid="confirm-amount">{fmtUsd(amount)}</p>
              <p className="mt-1 text-xs text-stone-400">{memo}</p>
              {me && <p className="mt-2 text-[11px] text-stone-500">from {me.label}'s wallet</p>}
              <div className="mt-3 flex items-center justify-center gap-2">
                <img src="/assets/diors/dior-apron-portrait.jpeg" alt="" className="w-7 h-7 rounded-full object-cover object-top" style={{ border: `1px solid ${TAN}` }} />
                <p className="text-[10px] text-stone-500">Dior is notified the moment this settles</p>
              </div>
            </div>
            <button
              data-testid="confirm-pay-button"
              onClick={doPay} disabled={busy}
              className="w-full rounded-xl py-4 text-sm font-bold uppercase tracking-widest disabled:opacity-50"
              style={{ background: CREAM, color: "#0c0b09" }}
            >
              {busy ? "Settling…" : me?.passkey_enrolled ? "Approve with Face ID" : "Pay now"}
            </button>
            <button onClick={() => setStep("shop")} className="w-full text-xs text-stone-500 hover:text-stone-300">← Back</button>
          </div>
        )}

        {/* guest Zelle traverse — coordinates + memo code, desk confirms, her wallet settles */}
        {step === "zelle" && zelle && (
          <div className="space-y-4 rounded-2xl border p-5" style={{ borderColor: TAN }} data-testid="zelle-checkout">
            <div className="flex justify-center">
              <img src={profile.logo} alt="" className="w-16 h-16 rounded-full object-cover" style={{ border: `2px solid ${TAN}` }} />
            </div>
            <div className="text-center">
              <p className="text-[10px] font-mono tracking-widest uppercase" style={{ color: TAN }}>Paying {profile.brand} by Zelle</p>
              <p className="mt-1 text-3xl font-bold" style={{ fontFamily: "Georgia, serif", color: CREAM }} data-testid="zelle-amount">{fmtUsd(zelle.amount_usd)}</p>
              <p className="mt-1 text-xs text-stone-400">{zelle.memo}</p>
            </div>
            <div className="rounded-xl border p-4 space-y-3" style={{ borderColor: "#3a352c", background: "rgba(255,255,255,0.02)" }}>
              {zelle.zelle_qr_b64 && (
                <div className="text-center" data-testid="zelle-qr-block">
                  <p className="text-[9px] font-mono tracking-widest uppercase text-stone-500 mb-2">fastest — scan with your phone camera</p>
                  <img src={zelle.zelle_qr_b64} alt="Zelle QR" data-testid="zelle-qr"
                       className="mx-auto w-40 h-40 rounded-xl bg-white p-2 object-contain" />
                  <p className="mt-1 text-[10px] text-stone-500">Your camera opens Zelle with her handle pre-filled — then set the amount and paste the code below.</p>
                </div>
              )}
              {zelle.zelle_pay_url && (
                <a href={zelle.zelle_pay_url} target="_blank" rel="noopener noreferrer" data-testid="zelle-pay-link"
                   className="block w-full text-center rounded-full py-3 text-sm font-bold uppercase tracking-widest text-stone-950"
                   style={{ background: TAN }}>
                  Open the Zelle payment link
                </a>
              )}
              <div>
                <p className="text-[9px] font-mono tracking-widest uppercase text-stone-500">1 · Zelle exactly {fmtUsd(zelle.amount_usd)} to</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="font-mono text-lg select-all" style={{ color: CREAM }} data-testid="zelle-handle">{zelle.zelle_handle}</p>
                  <button type="button" data-testid="zelle-copy-handle" onClick={() => copyText(zelle.zelle_handle, "handle")}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border" style={{ borderColor: TAN, color: TAN }}>
                    {copied === "handle" ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
              <div>
                <p className="text-[9px] font-mono tracking-widest uppercase text-stone-500">2 · Paste this code in the Zelle memo</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="font-mono text-2xl tracking-[0.2em] select-all" style={{ color: TAN }} data-testid="zelle-memo-code">{zelle.intention_id}</p>
                  <button type="button" data-testid="zelle-copy-code" onClick={() => copyText(zelle.intention_id, "code")}
                          className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border" style={{ borderColor: TAN, color: TAN }}>
                    {copied === "code" ? "Copied" : "Copy"}
                  </button>
                </div>
                <p className="mt-1 text-[10px] text-stone-500">{zelle.memo_rule}</p>
              </div>
              <p className="text-[10px] text-stone-500 leading-snug">
                3 · Send from your bank app (Chase, Wells Fargo, BofA — any Zelle). This page turns into your
                receipt the moment the desk confirms.
              </p>
            </div>
            <div className="flex items-center justify-center gap-2" data-testid="zelle-waiting">
              <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ background: TAN }} />
              <p className="text-[11px] text-stone-400">Waiting for the desk to confirm your Zelle…</p>
            </div>
            <div className="flex items-center justify-center gap-2">
              <img src="/assets/diors/dior-apron-portrait.jpeg" alt="" className="w-7 h-7 rounded-full object-cover object-top" style={{ border: `1px solid ${TAN}` }} />
              <p className="text-[10px] text-stone-500">Dior has already been texted that you're paying</p>
            </div>
            <button onClick={() => { setStep("shop"); setZelle(null); }} className="w-full text-xs text-stone-500 hover:text-stone-300" data-testid="zelle-back-button">← Back</button>
          </div>
        )}

        {/* Zelle settled — desk confirmed, her merchant wallet credited */}
        {step === "zelle-done" && zelle && (
          <div className="overflow-hidden rounded-2xl border" style={{ borderColor: TAN }} data-testid="zelle-receipt">
            <div className="relative h-24">
              <img src={profile.hero} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-[#0c0b09]" />
              <img src={profile.logo} alt="" className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full object-cover bg-[#0c0b09]"
                   style={{ border: `2px solid ${TAN}` }} />
            </div>
            <div className="pt-9 pb-6 px-6 text-center space-y-2" style={{ background: "rgba(201,160,108,0.05)" }}>
              <p className="text-lg font-bold" style={{ fontFamily: "Georgia, serif", color: CREAM }}>Zelle received — Dior has your order</p>
              <p className="text-3xl font-bold" style={{ color: CREAM }} data-testid="zelle-receipt-amount">{fmtUsd(zelle.amount_usd)}</p>
              <p className="text-xs text-stone-400">{zelle.memo}</p>
              <p className="text-[10px] font-mono text-stone-500" data-testid="zelle-receipt-ref">settled by the treasury desk · ref {zelle.intention_id}</p>
              <p className="text-[11px] text-stone-500 pt-1">Screenshot this for your records — @{profile.instagram} confirms preorders in DMs.</p>
              <div className="pt-2 flex items-center justify-center gap-2">
                <img src="/assets/diors/dior-apron-portrait.jpeg" alt="Dior" className="w-10 h-10 rounded-full object-cover object-top" style={{ border: `1px solid ${TAN}` }} />
                <p className="text-xs italic" style={{ fontFamily: "Georgia, serif", color: CREAM }}>— thank you, Dior</p>
              </div>
            </div>
          </div>
        )}

        {/* receipt */}
        {step === "receipt" && receipt && (
          <div className="overflow-hidden rounded-2xl border" style={{ borderColor: TAN }} data-testid="receipt-panel">
            <div className="relative h-24">
              <img src={profile.hero} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-[#0c0b09]" />
              <img src={profile.logo} alt="" className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full object-cover bg-[#0c0b09]"
                   style={{ border: `2px solid ${TAN}` }} />
            </div>
            <div className="pt-9 pb-6 px-6 text-center space-y-2" style={{ background: "rgba(201,160,108,0.05)" }}>
              <p className="text-lg font-bold" style={{ fontFamily: "Georgia, serif", color: CREAM }}>Paid — thank you!</p>
              <p className="text-3xl font-bold" style={{ color: CREAM }} data-testid="receipt-amount">{fmtUsd(receipt.amount_usd ?? amount)}</p>
              <p className="text-xs text-stone-400">{receipt.memo || memo}</p>
              {receipt.settlement_tx?.tx && (
                <p className="text-[10px] font-mono break-all text-stone-500" data-testid="receipt-tx">settled on-chain · {receipt.settlement_tx.tx}</p>
              )}
              {receipt.card_capture_id && (
                <p className="text-[10px] font-mono text-stone-500" data-testid="receipt-card-ref">
                  card payment · ref {receipt.card_capture_id}{receipt.env !== "live" ? " · TEST MODE — no money moved" : ""}
                </p>
              )}
              <p className="text-[11px] text-stone-500 pt-1">Screenshot this for your records — @{profile.instagram} confirms preorders in DMs.</p>
              <div className="pt-2 flex items-center justify-center gap-2">
                <img src="/assets/diors/dior-apron-portrait.jpeg" alt="Dior" className="w-10 h-10 rounded-full object-cover object-top" style={{ border: `1px solid ${TAN}` }} data-testid="receipt-baker" />
                <p className="text-xs italic" style={{ fontFamily: "Georgia, serif", color: CREAM }}>— thank you, Dior</p>
              </div>
            </div>
          </div>
        )}

        <footer className="mt-8 text-center space-y-1.5">
          <div className="flex items-center justify-center gap-2">
            <img src={profile.logo} alt="" className="w-5 h-5 rounded-full object-cover opacity-70" />
            <p className="text-[10px] font-mono tracking-widest uppercase text-stone-500">
              {profile.brand} · Henderson, NV
            </p>
          </div>
          <p className="text-[9px] font-mono tracking-widest uppercase text-stone-600">
            Wallet &amp; Zelle settle instantly in $sUSD on the Bourg Merchant Exchange · card payments settle to the PayPal balance
          </p>
          <p className="text-[9px] text-stone-700">
            Dior's Dough is an independent business and is not part of BGNZN Institute (501(c)(6)).
          </p>
          {/* underwriter-ready: every card processor's reviewer looks for exactly these lines */}
          <div className="pt-3 mt-3 space-y-1 border-t text-left" style={{ borderColor: "#262219" }} data-testid="storefront-policies">
            <p className="text-[9px] text-stone-600 leading-relaxed">
              <span className="text-stone-500 font-semibold">Contact:</span> DM @{profile.instagram} on Instagram — every order is confirmed personally.
            </p>
            <p className="text-[9px] text-stone-600 leading-relaxed">
              <span className="text-stone-500 font-semibold">Fulfillment:</span> baked to order — pickup or delivery timing is confirmed by DM after payment; out-of-state orders add $120.00 shipping &amp; handling.
            </p>
            <p className="text-[9px] text-stone-600 leading-relaxed">
              <span className="text-stone-500 font-semibold">Refunds:</span> if an order arrives wrong or not as described, DM within 24 hours for a replacement or a full refund.
            </p>
            <p className="text-[9px] text-stone-600 leading-relaxed">
              <span className="text-stone-500 font-semibold">Privacy:</span> your details are used only to fulfill your order. Card payments are processed by PayPal — this site never sees, stores, or transmits card numbers.
            </p>
          </div>
        </footer>
      </div>
      </div>
    </div>
  );
}
